import {
  App,
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ArrowRightOutlined, PlayCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  academicsApi,
  type ClassSectionLite,
  type ExecutePromotionsResult,
  type PromotionDecision,
  type PromotionPreviewResponse,
  type PromotionPreviewRow,
} from '@/api/academics';
import { setupApi } from '@/api/setup';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

interface RowState {
  decision: PromotionDecision;
  targetClassSectionId: number | null;
  reason: string;
}

const DECISIONS: { value: PromotionDecision; label: string; color: string }[] = [
  { value: 'PROMOTE', label: 'Promote', color: 'blue' },
  { value: 'REPEAT', label: 'Repeat', color: 'gold' },
  { value: 'SKIP', label: 'Skip class', color: 'purple' },
  { value: 'GRADUATE', label: 'Graduate', color: 'green' },
  { value: 'LEFT', label: 'Left', color: 'default' },
];

const NEEDS_TARGET = (d: PromotionDecision) => d === 'PROMOTE' || d === 'REPEAT' || d === 'SKIP';

export function PromotionPage() {
  const { message } = App.useApp();
  const canApprove = useAuthStore((s) => s.hasAuthority('ACADEMICS:APPROVE'));

  const [fromYearId, setFromYearId] = useState<number | undefined>();
  const [toYearId, setToYearId] = useState<number | undefined>();
  const [previewData, setPreviewData] = useState<PromotionPreviewResponse | null>(null);
  const [rowStates, setRowStates] = useState<Record<number, RowState>>({});
  const [search, setSearch] = useState('');
  const [resultModal, setResultModal] = useState<ExecutePromotionsResult | null>(null);

  const yearsQuery = useQuery({
    queryKey: ['setup', 'academic-years'],
    queryFn: () => setupApi.listAcademicYears(),
  });

  const preview = useMutation({
    mutationFn: () => academicsApi.previewPromotion(fromYearId!, toYearId!),
    onSuccess: (data) => {
      setPreviewData(data);
      const init: Record<number, RowState> = {};
      data.rows.forEach((r) => {
        init[r.studentId] = {
          decision: r.suggestedDecision,
          targetClassSectionId: r.suggestedClassSectionId,
          reason: '',
        };
      });
      setRowStates(init);
    },
    onError: (e) => message.error(extractError(e, 'Failed to load preview')),
  });

  const execute = useMutation({
    mutationFn: () =>
      academicsApi.executePromotion({
        fromAcademicYearId: fromYearId!,
        toAcademicYearId: toYearId!,
        decisions: previewData!.rows.map((r) => ({
          studentId: r.studentId,
          decision: rowStates[r.studentId].decision,
          targetClassSectionId: rowStates[r.studentId].targetClassSectionId,
          reason: rowStates[r.studentId].reason || null,
        })),
      }),
    onSuccess: (result) => {
      message.success('Promotion batch processed');
      setResultModal(result);
      setPreviewData(null);
      setRowStates({});
    },
    onError: (e) => message.error(extractError(e, 'Promotion failed')),
  });

  const filteredRows = useMemo(() => {
    if (!previewData) return [];
    const q = search.trim().toLowerCase();
    if (!q) return previewData.rows;
    return previewData.rows.filter(
      (r) =>
        r.studentName.toLowerCase().includes(q) ||
        r.registrationNumber.toLowerCase().includes(q) ||
        r.fromClassSectionName.toLowerCase().includes(q),
    );
  }, [previewData, search]);

  const counts = useMemo(() => {
    const c = { PROMOTE: 0, REPEAT: 0, SKIP: 0, GRADUATE: 0, LEFT: 0, missing: 0 };
    if (!previewData) return c;
    for (const r of previewData.rows) {
      const s = rowStates[r.studentId];
      if (!s) continue;
      c[s.decision]++;
      if (NEEDS_TARGET(s.decision) && !s.targetClassSectionId) c.missing++;
    }
    return c;
  }, [previewData, rowStates]);

  const setRow = (studentId: number, patch: Partial<RowState>) =>
    setRowStates((prev) => ({ ...prev, [studentId]: { ...prev[studentId], ...patch } }));

  const applySuggestedToAll = () => {
    if (!previewData) return;
    const next: Record<number, RowState> = {};
    previewData.rows.forEach((r) => {
      next[r.studentId] = {
        decision: r.suggestedDecision,
        targetClassSectionId: r.suggestedClassSectionId,
        reason: rowStates[r.studentId]?.reason ?? '',
      };
    });
    setRowStates(next);
  };

  const sectionsForRow = (row: PromotionPreviewRow): ClassSectionLite[] => {
    if (!previewData) return [];
    return previewData.targetSections;
  };

  const yearOptions =
    yearsQuery.data?.map((y) => ({ value: y.id, label: y.name + (y.current ? ' (current)' : '') })) ?? [];

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <PageHeader
        eyebrow="Academics"
        title="Year-end promotion"
        description="Bulk-promote a year group to next class. Set per-student exceptions (repeat, skip, graduate, left). Each promoted student gets a fresh enrollment in the target year and fee invoices are auto-generated."
      />

      <Card>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={9}>
            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
              From academic year
            </Typography.Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Choose source year"
              options={yearOptions}
              value={fromYearId}
              onChange={(v) => {
                setFromYearId(v);
                setPreviewData(null);
              }}
              loading={yearsQuery.isLoading}
            />
          </Col>
          <Col xs={24} md={1} style={{ textAlign: 'center' }}>
            <ArrowRightOutlined style={{ fontSize: 18 }} />
          </Col>
          <Col xs={24} md={9}>
            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
              To academic year
            </Typography.Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Choose target year"
              options={yearOptions.filter((o) => o.value !== fromYearId)}
              value={toYearId}
              onChange={(v) => {
                setToYearId(v);
                setPreviewData(null);
              }}
              loading={yearsQuery.isLoading}
            />
          </Col>
          <Col xs={24} md={5}>
            <Button
              type="primary"
              block
              icon={<ReloadOutlined />}
              loading={preview.isPending}
              disabled={!fromYearId || !toYearId || fromYearId === toYearId}
              onClick={() => preview.mutate()}
            >
              {previewData ? 'Reload preview' : 'Load preview'}
            </Button>
          </Col>
        </Row>
      </Card>

      {preview.isPending && (
        <Card>
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin />
          </div>
        </Card>
      )}

      {previewData && previewData.rows.length === 0 && (
        <Card>
          <Empty description="No active students with enrollments to promote in the source year." />
        </Card>
      )}

      {previewData && previewData.rows.length > 0 && (
        <>
          <Card>
            <Row gutter={[16, 12]} align="middle">
              <Col xs={12} md={4}>
                <Statistic title="Promote" value={counts.PROMOTE} styles={{ content: { color: '#1677ff' } }} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="Repeat" value={counts.REPEAT} styles={{ content: { color: '#d48806' } }} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="Skip" value={counts.SKIP} styles={{ content: { color: '#722ed1' } }} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="Graduate" value={counts.GRADUATE} styles={{ content: { color: '#389e0d' } }} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="Left" value={counts.LEFT} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic
                  title="Missing target"
                  value={counts.missing}
                  styles={{ content: { color: counts.missing > 0 ? '#cf1322' : undefined } }}
                />
              </Col>
            </Row>
          </Card>

          <Card
            title={
              <Space>
                <Typography.Text strong>{previewData.rows.length} students</Typography.Text>
                <Badge color="green" text={`${filteredRows.length} shown`} />
              </Space>
            }
            extra={
              <Space>
                <Input.Search
                  placeholder="Search name / reg # / class"
                  allowClear
                  onSearch={setSearch}
                  onChange={(e) => setSearch(e.target.value)}
                  value={search}
                  style={{ width: 260 }}
                />
                <Button onClick={applySuggestedToAll}>Reset to suggested</Button>
                <Popconfirm
                  title="Execute promotion?"
                  description={
                    <div style={{ maxWidth: 320 }}>
                      <p style={{ margin: 0 }}>
                        This will deactivate {previewData.rows.length} enrollments in the source year, create
                        new enrollments + fee invoices for {counts.PROMOTE + counts.REPEAT + counts.SKIP} students,
                        and mark {counts.GRADUATE} graduated, {counts.LEFT} left.
                      </p>
                      <p style={{ margin: '6px 0 0 0', color: '#cf1322' }}>
                        Cannot be undone in bulk — individual rows are reversible by manually adjusting enrollments.
                      </p>
                    </div>
                  }
                  okText="Execute"
                  okButtonProps={{ danger: true, loading: execute.isPending }}
                  disabled={!canApprove || counts.missing > 0}
                  onConfirm={() => execute.mutate()}
                >
                  <Button
                    type="primary"
                    icon={<PlayCircleOutlined />}
                    danger
                    disabled={!canApprove || counts.missing > 0}
                  >
                    Execute promotion
                  </Button>
                </Popconfirm>
              </Space>
            }
          >
            {counts.missing > 0 && (
              <Alert
                type="warning"
                showIcon
                message={`${counts.missing} row(s) need a target class·section before you can execute.`}
                style={{ marginBottom: 12 }}
              />
            )}
            {!canApprove && (
              <Alert
                type="info"
                showIcon
                message="You have view access only. Executing requires the ACADEMICS:APPROVE permission."
                style={{ marginBottom: 12 }}
              />
            )}
            <Table
              rowKey="studentId"
              dataSource={filteredRows}
              size="small"
              pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: [25, 50, 100] }}
              columns={[
                { title: 'Reg #', dataIndex: 'registrationNumber', width: 110, fixed: 'left' as const },
                {
                  title: 'Student',
                  dataIndex: 'studentName',
                  render: (v: string) => <Typography.Text>{v}</Typography.Text>,
                  width: 200,
                  fixed: 'left' as const,
                },
                {
                  title: 'Current class',
                  dataIndex: 'fromClassSectionName',
                  width: 160,
                  render: (v: string) => <Tag>{v}</Tag>,
                },
                {
                  title: 'Decision',
                  dataIndex: 'studentId',
                  width: 170,
                  render: (sid: number) => {
                    const state = rowStates[sid];
                    if (!state) return null;
                    return (
                      <Select
                        style={{ width: '100%' }}
                        value={state.decision}
                        onChange={(v) =>
                          setRow(sid, {
                            decision: v,
                            targetClassSectionId: NEEDS_TARGET(v) ? state.targetClassSectionId : null,
                          })
                        }
                        options={DECISIONS.map((d) => ({
                          value: d.value,
                          label: <Tag color={d.color}>{d.label}</Tag>,
                        }))}
                      />
                    );
                  },
                },
                {
                  title: 'Target class · section',
                  dataIndex: 'studentId',
                  width: 220,
                  render: (sid: number, row: PromotionPreviewRow) => {
                    const state = rowStates[sid];
                    if (!state) return null;
                    if (!NEEDS_TARGET(state.decision)) {
                      return <Typography.Text type="secondary">—</Typography.Text>;
                    }
                    return (
                      <Select
                        style={{ width: '100%' }}
                        value={state.targetClassSectionId ?? undefined}
                        placeholder="Choose target"
                        onChange={(v) => setRow(sid, { targetClassSectionId: v })}
                        options={sectionsForRow(row).map((cs) => ({ value: cs.id, label: cs.name }))}
                        showSearch
                        filterOption={(i, o) =>
                          (o?.label as string)?.toLowerCase().includes(i.toLowerCase()) ?? false
                        }
                        status={state.targetClassSectionId ? undefined : 'error'}
                      />
                    );
                  },
                },
                {
                  title: 'Reason',
                  dataIndex: 'studentId',
                  render: (sid: number) => {
                    const state = rowStates[sid];
                    if (!state) return null;
                    return (
                      <Input
                        size="small"
                        placeholder="Optional — required-looking for repeat/skip"
                        value={state.reason}
                        onChange={(e) => setRow(sid, { reason: e.target.value })}
                        maxLength={1000}
                      />
                    );
                  },
                },
              ]}
              scroll={{ x: 1100 }}
            />
          </Card>
        </>
      )}

      <Modal
        open={!!resultModal}
        onCancel={() => setResultModal(null)}
        footer={[
          <Button key="close" type="primary" onClick={() => setResultModal(null)}>
            Done
          </Button>,
        ]}
        title="Promotion result"
        width={520}
      >
        {resultModal && (
          <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
            <Row gutter={[12, 12]}>
              <Col xs={12} sm={12}>
                <Statistic title="Total processed" value={resultModal.total} />
              </Col>
              <Col xs={12} sm={12}>
                <Statistic title="Promoted" value={resultModal.promoted} styles={{ content: { color: '#1677ff' } }} />
              </Col>
              <Col xs={12} sm={12}>
                <Statistic title="Repeated" value={resultModal.repeated} styles={{ content: { color: '#d48806' } }} />
              </Col>
              <Col xs={12} sm={12}>
                <Statistic title="Skipped" value={resultModal.skipped} styles={{ content: { color: '#722ed1' } }} />
              </Col>
              <Col xs={12} sm={12}>
                <Statistic title="Graduated" value={resultModal.graduated} styles={{ content: { color: '#389e0d' } }} />
              </Col>
              <Col xs={12} sm={12}>
                <Statistic title="Left" value={resultModal.leftCount} />
              </Col>
            </Row>
            {resultModal.failed > 0 && (
              <Alert type="error" showIcon message={`${resultModal.failed} row(s) failed`} />
            )}
          </Space>
        )}
      </Modal>
    </div>
  );
}
